/**
 * Rate Limit Handler
 * Handles API rate limiting with exponential backoff and retry logic
 */

class RateLimitHandler {
  constructor() {
    this.retryDelays = [500, 1000, 2000]; // Faster, shorter delays: 0.5s, 1s, 2s
    this.activeRequests = new Map(); // Track active requests to prevent duplicates
  }

  /**
   * Execute a request with rate limit handling
   * @param {Function} requestFn - Function that makes the API request
   * @param {string} requestKey - Unique key for this request (to prevent duplicates)
   * @param {number} maxRetries - Maximum number of retries (default: 1)
   */
  async executeWithRetry(requestFn, requestKey, maxRetries = 1) {
    // Check if this request is already in progress
    if (this.activeRequests.has(requestKey)) {
      // console.log(`Request ${requestKey} already in progress, waiting...`);
      return this.activeRequests.get(requestKey);
    }

    const requestPromise = this._executeRequest(requestFn, maxRetries);
    this.activeRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.activeRequests.delete(requestKey);
    }
  }

  /**
   * Internal method to execute request with retry logic
   */
  async _executeRequest(requestFn, maxRetries) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await requestFn();
        
        // If successful, reset any rate limit tracking for this endpoint
        return result;
      } catch (error) {
        lastError = error;

        // Check if it's a rate limit error (429)
        if (error.response?.status === 429) {
          if (attempt < maxRetries) {
            const delay = this.retryDelays[attempt] || this.retryDelays[this.retryDelays.length - 1];
            
            // console.warn(`Rate limit hit (attempt ${attempt + 1}/${maxRetries + 1}). Retrying in ${delay}ms...`);
            
            // Extract retry-after header if available
            const retryAfter = error.response?.headers['retry-after'];
            const actualDelay = retryAfter ? parseInt(retryAfter) * 1000 : delay;
            
            await this.delay(actualDelay);
            continue;
          } else {
            // Max retries reached
            throw new Error(`Rate limit exceeded. Please wait a moment and try again. (Max retries: ${maxRetries})`);
          }
        }

        // For non-rate-limit errors, don't retry
        throw error;
      }
    }

    throw lastError;
  }

  /**
   * Delay utility
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get a unique key for a request
   */
  getRequestKey(method, url, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    
    return `${method}:${url}${paramString ? '?' + paramString : ''}`;
  }

  /**
   * Clear all active requests (useful for cleanup)
   */
  clearActiveRequests() {
    this.activeRequests.clear();
  }
}

// Create singleton instance
export const rateLimitHandler = new RateLimitHandler();

export default rateLimitHandler;